import wrapperHOC from "@Utils/wrapperHOC";
import { memo, ReactNode, useEffect, useState } from "react";
import styles from "./ContentSidebar.module.scss";
import Image from "@Components/Image";
import useIsDesktop from "hooks/useIsDesktop";
import CustomLink from "@Components/CustomLink";
import Skeleton from "@Components/Skeleton";

export interface SidebarFeaturedItem {
  id: number;
  name: string;
  slug: string;
  mainImage: {
    url: string;
    alt: string;
  };
  category: {
    name: string;
  };
  readTime?: string;
  date: string;
  updatedAt?: string;
}

interface ContentSidebarProps {
  featuredItems: SidebarFeaturedItem[];
  basePath: string;
  featuredTitle: string;
  bannerImageUrl: string;
  bannerAlt: string;
  utmSource: string;
  partnersComponent?: ReactNode;
  inventoryCarouselComponent?: ReactNode;
  hideTopDivider?: boolean;
  hideFeaturedSection?: boolean;
}

const useIsMounted = () => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  return mounted;
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

const ContentSidebar = ({
  featuredItems,
  basePath,
  featuredTitle,
  bannerImageUrl,
  bannerAlt,
  utmSource,
  partnersComponent,
  inventoryCarouselComponent,
  hideTopDivider = false,
  hideFeaturedSection: hideFeaturedSectionProp = false,
}: ContentSidebarProps) => {
  // Hide featured section whenever the inventory carousel is present
  const hideFeaturedSection = hideFeaturedSectionProp || Boolean(inventoryCarouselComponent);
  const isDesktop = useIsDesktop();
  const isMounted = useIsMounted();

  const bannerUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/?utm_source=${utmSource}&utm_medium=lead_gen&utm_campaign=banner`
      : `/?utm_source=${utmSource}&utm_medium=lead_gen&utm_campaign=banner`;

  return (
    <div className={styles.sidebar}>
      {!isDesktop && !hideTopDivider && <div className={styles.horizontalDivider} />}
      {!isDesktop && hideTopDivider && <div className={styles.topSpacer} />}

      {!hideFeaturedSection && (
        <>
          <div className={styles.featuredSection}>
            <div className={styles.featuredHeader}>
              <h3 className={styles.featuredTitle}>{featuredTitle}</h3>
              <div className={styles.horizontalLine} />
            </div>
            <div className={styles.featuredList}>
              {featuredItems.map((item, index) => (
                <div key={item.id}>
                  <CustomLink
                    href={`${basePath}/post/${item.slug}`}
                    className={styles.featuredItem}
                  >
                    <div className={styles.featuredImage}>
                      <Image
                        src={`${item.mainImage.url}?w=400`}
                        alt={item.mainImage.alt}
                        width="123"
                        height="95"
                      />
                    </div>
                    <div className={styles.featuredContent}>
                      <span className={styles.badge}>{item.category.name}</span>
                      <h4 className={styles.featuredItemTitle}>{item.name}</h4>
                      <div className={styles.featuredItemMeta}>
                        <span>{item.readTime}</span>
                        <span className={styles.separator}>·</span>
                        <span>
                          {formatDate(
                            basePath === "/news"
                              ? item.date || item.updatedAt || ""
                              : item.updatedAt || item.date,
                          )}
                        </span>
                      </div>
                    </div>
                  </CustomLink>
                  {index < featuredItems.length - 1 && (
                    <div className={styles.featuredItemDivider} />
                  )}
                </div>
              ))}
            </div>
          </div>

          {!isDesktop && <div className={styles.horizontalDivider} />}
        </>
      )}

      {partnersComponent && (
        <>
          <div className={styles.partnersContainer}>
            {isMounted ? (
              partnersComponent
            ) : (
              <Skeleton width="100%" height="auto" addBorderRadius>
                <div className={styles.partnersSkeleton} />
              </Skeleton>
            )}
          </div>
          {!isDesktop && <div className={styles.horizontalDivider} />}
        </>
      )}

      {!inventoryCarouselComponent && (
        <>
          <div className={styles.stickyWrapper}>
            <CustomLink
              href={bannerUrl}
              className={styles.sidebarImage}
              target="_blank"
              rel="noopener noreferrer"
              isExternal
            >
              <Image src={bannerImageUrl} alt={bannerAlt} width="366" height="auto" />
            </CustomLink>
          </div>
          {!isDesktop && <div className={styles.horizontalDivider} />}
        </>
      )}

      {inventoryCarouselComponent && (
        <>
          <div className={styles.stickyWrapper}>{inventoryCarouselComponent}</div>
          {!isDesktop && <div className={styles.horizontalDivider} />}
        </>
      )}
    </div>
  );
};

export default wrapperHOC(memo(ContentSidebar), {
  componentName: "ContentSidebar",
  showForChina: true,
});
